import React, { useState, useEffect } from 'react';
import { getConsentBranding } from '../../utils/consentBranding';
import { addLead, getOrganizations, getTeamsByOrganization, getUserSubTeamInfo, sendEmail } from '../../api';
import { useNavigate, useLocation } from 'react-router-dom';
import SignatureField from '../../components/SignatureField';
import OptInToggle from '../../components/common/OptInToggle';

function WpForm({ user, onCancel, onLeadAdded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');

  const [step, setStep] = useState(1);
  const [organizationName, setOrganizationName] = useState('');
  
  const [propertyData, setPropertyData] = useState({
    isOwner: null, // null = nicht ausgewählt, true/false für Ja/Nein
    buildingType: '', // 'Einfamilienhaus', 'Zweifamilienhaus', 'Reihenhaus'
    livingAreaSize: '', // Wohnfläche in Quadratmetern als String
    peopleInHouse: '', // Anzahl der Personen im Haus als String
    buildYear: '', // Baujahr des Hauses als String
    hasGasOrOil: null, // null = nicht ausgewählt, true/false für Ja/Nein
    heatingOlderThan2Years: null, // null = nicht ausgewählt, true/false für Ja/Nein
    currentHeatingSystem: '', // 'Fußbodenheizung', 'Heizkörper', 'Beides'
    hasSolarThermal: null, // null = nicht ausgewählt, true/false für Ja/Nein
    solarThermalRemoved: null, // null = nicht ausgewählt, true/false für Ja/Nein
    hasMonumentProtection: null, // null = nicht ausgewählt, true/false für Ja/Nein
  });
  
  const [formData, setFormData] = useState({
    produkt: 'WP',
    signature: '', // Änderung: optIn -> signature
    dienstleister: user?.full_name || '',
    anrede: '',
    name: '',
    nachname: '',
    email: '',
    handy: '',
    strasse: '',        
    hausnummer: '',       
    postleitzahl: '',   
    stadt: '',          
  });

  // Opt-In Toggle State
  const [optInMode, setOptInMode] = useState('signature'); // 'signature' oder 'email'
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamName, setTeamName] = useState('');
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  // Fortschritt der progressiven Offenlegung
  const [progression, setProgression] = useState(1);

  // Organisationsname laden
  useEffect(() => {
  const loadTeamName = async () => {
    if (user && user.id && user.organization_id && user.team_id) {
      try {
        // Sub-Team-Informationen laden
        const subTeamResponse = await getUserSubTeamInfo(user.id);
        let projectName = '';
        
        if (subTeamResponse.success && subTeamResponse.userInfo) {
          const userInfo = subTeamResponse.userInfo;
          
          // Wenn Benutzer in einem Sub-Team ist, zeige "Team-Name (Sub-Team-Name)"
          if (userInfo.sub_team_name) {
            projectName = `${userInfo.team_name} (${userInfo.sub_team_name})`;
          } else {
            // Wenn nur Team, zeige nur Team-Name
            projectName = userInfo.team_name;
          }
        } else {
          // Fallback: Team-Informationen laden
          const response = await getTeamsByOrganization(user.id, user.organization_id);
          if (response.success) {
            const userTeam = response.teams.find(team => team.id === user.team_id);
            if (userTeam) {
              projectName = userTeam.name;
            } else {
              projectName = 'Mein Team';
            }
          } else {
            projectName = user.team?.name || 'Unbekanntes Team';
          }
        }
        
        setTeamName(projectName);
        setFormData(prev => ({
          ...prev,
          projekt: projectName,
          dienstleister: user.full_name || user.email || ''
        }));
      } catch (err) {
        console.error('Fehler beim Laden der Team/Sub-Team-Informationen:', err);
        // Fallback bei Netzwerkfehler
        setTeamName(user.team?.name || 'Mein Team');
        setFormData(prev => ({
          ...prev,
          projekt: user.team?.name || 'Mein Team',
          dienstleister: user.full_name || user.email || ''
        }));
      }
    } else {
      // Fallback: Wenn keine Team-ID vorhanden, versuche Organization zu laden
      await loadOrganizationName();
    }
  };
  
  const loadOrganizationName = async () => {
    if (user && user.id && user.organization_id) {
      try {
        const response = await getOrganizations(user.id);
        if (response.success) {
          const userOrg = response.organizations.find(org => org.id === user.organization_id);
          if (userOrg) {
            setOrganizationName(userOrg.name);
            // Projekt automatisch mit Organisationsname setzen (Fallback)
            setFormData(prev => ({
              ...prev,
              projekt: userOrg.name,
              dienstleister: user.full_name || user.email || ''
            }));
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden der Organisation:', err);
      }
    }
  };
  
  loadTeamName();
}, [user]);

  // Handler für die Eigentumsauswahl (Ja/Nein)
  const handleOwnershipChange = (isOwner) => {
    setPropertyData({
      ...propertyData,
      isOwner: isOwner
    });
    
    // Erhöhe die Progression, wenn Option gewählt wurde
    if (progression === 1) {
      setProgression(2);
    }
  };

  // Handler für die Gebäudeart
  const handleBuildingTypeChange = (type) => {
    setPropertyData({
      ...propertyData,
      buildingType: type
    });
    
    // Erhöhe die Progression, wenn Option gewählt wurde
    if (progression === 2) {
      setProgression(3);
    }
  };

  // Handler für Wohnfläche vom Haus
  const handleLivingAreaSizeChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      livingAreaSize: value,
    });
    
    // Erhöhe die Progression, wenn ein Wert eingegeben wurde
    if (progression === 3 && value.trim() !== '') {
      setProgression(4);
    }
  };

  // Handler für Personen im Haus
  const handlePeopleInHouseChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      peopleInHouse: value
    });
    
    // Erhöhe die Progression, wenn ein Wert eingegeben wurde
    if (progression === 4 && value.trim() !== '') {
      setProgression(5);
    }
  };
  
  // Handler für Baujahr
  const handleBuildYearChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      buildYear: value
    });
    
    // Erhöhe die Progression
    if (progression === 5 && value.trim() !== '') {
      setProgression(6);
    }
  };

  // Handler für Gas oder Öl Heizung
  const handleGasOrOilChange = (hasGasOrOil) => {
    setPropertyData({
      ...propertyData,
      hasGasOrOil: hasGasOrOil
    });
    
    // Erhöhe die Progression
    if (progression === 6) {
      setProgression(7);
    }
  };

  // Handler für Heizung älter als 2 Jahre
  const handleHeatingOlderThan2YearsChange = (isOlderThan2Years) => {
    setPropertyData({
      ...propertyData,
      heatingOlderThan2Years: isOlderThan2Years
    });
    
    // Erhöhe die Progression
    if (progression === 7) {
      setProgression(8);
    }
  };

  const handleCurrentHeatingSystemChange = (system) => {
    setPropertyData({
      ...propertyData,
      currentHeatingSystem: system
    });
    
    // Erhöhe die Progression
    if (progression === 8) {
      setProgression(9);
    }
  };

  const handleSolarThermalChange = (hasSolarThermal) => {
    setPropertyData({
      ...propertyData,
      hasSolarThermal: hasSolarThermal,
      // Zurücksetzen der Folge-Frage, wenn man von Ja auf Nein wechselt
      solarThermalRemoved: hasSolarThermal ? propertyData.solarThermalRemoved : null
    });
    
    // Progression aktualisieren - auch wenn bereits auf 10 oder höher
    if (progression >= 9) {
      if (hasSolarThermal) {
        setProgression(10);
      } else {
        setProgression(11);
      }
    }
  };

  // Handler für Solarthermie wird abgeklemmt oder abgebaut
  const handleSolarThermalRemovedChange = (solarThermalRemoved) => {
    setPropertyData({
      ...propertyData,
      solarThermalRemoved: solarThermalRemoved
    });
    
    // Erhöhe die Progression
    if (progression === 10) {
      setProgression(11);
    }
  };

  // Handler für Denkmalschutz
  const handleMonumentProtectionChange = (hasProtection) => {
    setPropertyData({
      ...propertyData,
      hasMonumentProtection: hasProtection
    });
    
    // Erhöhe die Progression am Ende
    if (progression === 11) {
      setProgression(12);
    }
  };
  
  // Handler für Änderungen in Schritt 2 (Kundendaten) - nur für editierbare Felder
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Erlaubte Felder erweitern (signature entfernt aus der Liste)
    if (['anrede', 'name', 'nachname', 'email', 'handy', 'strasse', 'hausnummer', 'postleitzahl', 'stadt'].includes(name)) {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Neuer Handler für Unterschrift
  const handleSignatureChange = (signatureData) => {
    setFormData({
      ...formData,
      signature: signatureData
    });
  };

  // Opt-In Toggle Handler
  const handleOptInModeChange = (mode) => {
    setOptInMode(mode);
    // Wenn auf E-Mail umgestellt wird, Unterschrift zurücksetzen
    if (mode === 'email') {
      setFormData(prev => ({
        ...prev,
        signature: ''
      }));
    }
  };

  const handleOptInSignatureChange = (signatureData) => {
    setFormData(prev => ({
      ...prev,
      signature: signatureData
    }));
  };

  // Wechsel zum nächsten Schritt
  const nextStep = () => {
    setStep(step + 1);
  };

  // Zurück zum vorherigen Schritt
  const prevStep = () => {
    setStep(step - 1);
  };

  const isStep1Valid = () => {
    return (
      propertyData.isOwner === true &&
      propertyData.buildingType !== '' &&
      propertyData.livingAreaSize.trim() !== '' &&
      propertyData.peopleInHouse.trim() !== '' &&
      propertyData.buildYear.trim() !== '' &&
      propertyData.hasGasOrOil !== null &&
      propertyData.heatingOlderThan2Years !== null &&
      propertyData.currentHeatingSystem !== '' &&
      propertyData.hasSolarThermal !== null &&
      (propertyData.hasSolarThermal === false || 
       (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved !== null)) &&
      propertyData.hasMonumentProtection !== null
    );
  };

  const isLivingAreaUnsupported = () => {
    return Number(propertyData.livingAreaSize) > 250;
  };
  
  // Prüfen, ob Lead qualifiziert ist (entsprechend den Kriterien)
  const isLeadQualified = () => {
    return (
      propertyData.isOwner === true &&
      !isLivingAreaUnsupported() &&
      propertyData.hasGasOrOil === true &&
      propertyData.heatingOlderThan2Years === true &&
      propertyData.hasMonumentProtection === false &&
      (propertyData.hasSolarThermal === false || 
       (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved === true))
    );
  };
  
  // Prüfen, ob Lead disqualifiziert ist (entsprechend den Kriterien)
  const isLeadDisqualified = () => {
    // Disqualifizierungskriterien
    if (propertyData.isOwner === false) return true;
    if (isLivingAreaUnsupported()) return true;
    if (propertyData.hasGasOrOil === false) return true;
    if (propertyData.heatingOlderThan2Years === false) return true;
    if (propertyData.hasMonumentProtection === true) return true;
    if (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved === false) return true;
    
    // Keine Disqualifizierung
    return false;
  };

  // Funktion zum Generieren der Template-Email
  const generateTemplateEmail = (vorname, nachname) => {
    const cleanVorname = (vorname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
    const cleanNachname = (nachname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
    if (cleanVorname && cleanNachname) {
      return `${cleanVorname}.${cleanNachname}@keinemail.de`;
    }
    return '';
  };

  // Absenden des Formulars mit allen Daten
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Prüfen ob Email leer ist für WP Leads
    if (!formData.email && formData.name && formData.nachname) {
      // Email ist leer, Warnung anzeigen
      setPendingSubmit(e);
      setShowEmailWarningModal(true);
      return;
    }

    // Wenn Email leer ist aber keine Vor- und Nachnamen vorhanden, direkt absenden
    await submitLeadData();
  };

  // Eigentliche Submit-Funktion
  const submitLeadData = async (emailOverride = null) => {
    setLoading(true);

    try {
      // Kombiniere alle Daten mit den korrekten Feldnamen für die WP-Tabelle
      const combinedData = {
        userId: user.id,
        ...formData,
        // Email-Override falls vorhanden (für Template-Email)
        email: emailOverride !== null ? emailOverride : formData.email,
        // Immobiliendaten mit den korrekten Feldnamen für wp_lead_properties
        isOwner: propertyData.isOwner,
        buildingType: propertyData.buildingType,
        livingAreaSize: propertyData.livingAreaSize,
        peopleInHouse: propertyData.peopleInHouse,
        buildYear: propertyData.buildYear,
        hasGasOrOil: propertyData.hasGasOrOil,
        heatingOlderThan2Years: propertyData.heatingOlderThan2Years,
        currentHeatingSystem: propertyData.currentHeatingSystem,
        hasSolarThermal: propertyData.hasSolarThermal,
        solarThermalRemoved: propertyData.solarThermalRemoved,
        hasMonumentProtection: propertyData.hasMonumentProtection,
        isQualified: isLeadQualified() // Füge Qualifizierungsstatus hinzu
      };

      const data = await addLead(combinedData);
    
      if (data.success) {
        setSuccess('Lead erfolgreich hinzugefügt!');

        // Opt-In E-Mail senden, wenn E-Mail-Modus gewählt wurde
        if (optInMode === 'email' && formData.email) {
          try {
            const emailResponse = await sendEmail({
              emailType: 'optin_confirmation',
              recipientEmail: formData.email,
              recipientName: formData.name,
              userId: user.id,
              organizationId: user.organization_id || 4
            });

            if (emailResponse.success) {
              setSuccess('Lead erfolgreich hinzugefügt und Opt-In E-Mail gesendet!');
            } else {
              setSuccess('Lead erfolgreich hinzugefügt, aber Opt-In E-Mail konnte nicht gesendet werden: ' + emailResponse.error);
            }
          } catch (emailError) {
            setSuccess('Lead erfolgreich hinzugefügt, aber Opt-In E-Mail konnte nicht gesendet werden: ' + emailError.message);
          }
        }

        // Lead-Liste aktualisieren
        if (onLeadAdded) {
          onLeadAdded();
        }
        
        // Nach 2 Sekunden zur Leads-Liste zurückkehren
        setTimeout(() => {
          if (customerId) {
            navigate(-1); // oder navigate(`/customer/${customerId}`);
          } else if (onCancel) {
            onCancel();
          } else {
            navigate('/dashboard');
          }
        }, 2000);
      } else {
        setError(data.error || 'Fehler beim Hinzufügen des Leads');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler für Abbrechen
  const handleCancel = () => {
    if (customerId) {
      navigate(-1); // Zurück zur vorherigen Seite
    } else if (onCancel) {
      onCancel();
    } else {
      navigate('/dashboard');
    }
  };

  // Rendering des ersten Schritts - WP Anlage Daten mit progressiver Offenlegung
  const renderStep1 = () => {
    return (
      <div className="wp-form-step">
        <h2 className="form-title">WP Anlage</h2>
        
        {/* Eigentümer - Immer sichtbar als erstes Element */}
        <div className="form-section">
          <label className="form-label required">Eigentümer</label>
          <div className="yes-no-buttons">
            <button 
              type="button"
              className={`yes-button ${propertyData.isOwner === true ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(true)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="yes-icon">
                  <path 
                    d="M4 12.6L9 17.6L20 6.6" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
              </div>
              <span>Ja</span>
            </button>
            <button 
              type="button"
              className={`no-button ${propertyData.isOwner === false ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(false)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="no-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
              </div>
              <span>Nein</span>
            </button>
          </div>
          
          {/* Qualifizierungsnachricht für Eigentümer */}
          {propertyData.isOwner === false && (
            <div className="qualification-message disqualified">
              <svg viewBox="0 0 24 24" className="qualification-icon">
                <path 
                  d="M5 5L19 19M5 19L19 5" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </svg>
              <span>Lead nicht Qualifiziert</span>
            </div>
          )}
        </div>
        
        {progression >= 2 && (
          <div className="form-section">
            <label className="form-label required">Gebäudeart</label>
            <div className="building-type-buttons">
              <button 
                type="button"
                className={`building-button ${propertyData.buildingType === 'Einfamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Einfamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L20,45 L20,80 L80,80 L80,45 Z" fill="none" stroke="currentColor" strokeWidth="3"/>
                    <rect x="35" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="55" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="45" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Einfamilienhaus</span>
              </button>
              <button 
                type="button"
                className={`building-button ${propertyData.buildingType === 'Zweifamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Zweifamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L15,45 L15,80 L85,80 L85,45 Z" fill="none" stroke="currentColor" strokeWidth="3"/>
                    <line x1="50" y1="45" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
                    <rect x="25" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="65" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="30" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="60" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Zweifamilienhaus</span>
              </button>
              <button 
                type="button"
                className={`building-button ${propertyData.buildingType === 'Reihenhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Reihenhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M10,80 L10,45 L30,30 L30,80 Z M30,80 L30,30 L50,30 L50,80 Z M50,80 L50,30 L70,30 L70,80 Z M70,80 L70,30 L90,45 L90,80 Z" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="15" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="37" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="56" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="77" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <span>Reihenhaus</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Wohnfläche */}
        {progression >= 3 && (
          <div className="form-section">
            <label className="form-label required">Wie groß ist die Wohnfläche Ihres Hauses?</label>
            <input
              type="number"
              name="livingAreaSize"
              value={propertyData.livingAreaSize}
              onChange={handleLivingAreaSizeChange}
              className="form-input"
              placeholder="z.B. 120"
              required
            />
            {isLivingAreaUnsupported() && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
                <span>Lead nicht Qualifiziert - Wohnfläche über 250 qm wird nicht unterstützt</span>
              </div>
            )}
          </div>
        )}
        
        {progression >= 4 && (
          <div className="form-section">
            <label className="form-label required">Wie viele Personen Leben in Ihrem Haus?</label>
            <input
              type="number"
              name="peopleInHouse"
              value={propertyData.peopleInHouse}
              onChange={handlePeopleInHouseChange}
              className="form-input"
              placeholder="z.B. 4"
              required
            />
          </div>
        )}

        {/* Baujahr */}
        {progression >= 5 && (
          <div className="form-section">
            <label className="form-label required">Wann wurde Ihr Haus gebaut?</label>
            <input
              type="number"
              name="buildYear"
              value={propertyData.buildYear}
              onChange={handleBuildYearChange}
              className="form-input"
              placeholder="z.B. 1985"
              required
            />
          </div>
        )}

        {/* Gas oder Öl Heizung */}
        {progression >= 6 && (
          <div className="form-section">
            <label className="form-label required">Haben Sie eine Gas, Öl oder Wärmepumpen Heizung?</label>
            <div className="yes-no-buttons">
              <button 
                type="button"
                className={`yes-button ${propertyData.hasGasOrOil === true ? 'selected' : ''}`}
                onClick={() => handleGasOrOilChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path 
                      d="M4 12.6L9 17.6L20 6.6" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button 
                type="button"
                className={`no-button ${propertyData.hasGasOrOil === false ? 'selected' : ''}`}
                onClick={() => handleGasOrOilChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path 
                      d="M5 5L19 19M5 19L19 5" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            
            {/* Qualifizierungsnachricht */}
            {propertyData.hasGasOrOil === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}
        
        {/* Heizungsanlage älter als 2 Jahren */}
        {progression >= 7 && (
          <div className="form-section">
            <label className="form-label required">Heizungsanlage älter als 10 Jahre?</label>
            <div className="yes-no-buttons">
              <button 
                type="button"
                className={`yes-button ${propertyData.heatingOlderThan2Years === true ? 'selected' : ''}`}
                onClick={() => handleHeatingOlderThan2YearsChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path 
                      d="M4 12.6L9 17.6L20 6.6" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button 
                type="button"
                className={`no-button ${propertyData.heatingOlderThan2Years === false ? 'selected' : ''}`}
                onClick={() => handleHeatingOlderThan2YearsChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path 
                      d="M5 5L19 19M5 19L19 5" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            
            {/* Qualifizierungsnachricht */}
            {propertyData.heatingOlderThan2Years === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Aktuelles Wärmeabgabesystem */}
        {progression >= 8 && (
          <div className="form-section">
            <label className="form-label required">Aktuelles Wärmeabgabesystem</label>
            <div className="radio-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Fußbodenheizung"
                  checked={propertyData.currentHeatingSystem === "Fußbodenheizung"}
                  onChange={() => handleCurrentHeatingSystemChange("Fußbodenheizung")}
                />
                <span>Fußbodenheizung</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Heizkörper"
                  checked={propertyData.currentHeatingSystem === "Heizkörper"}
                  onChange={() => handleCurrentHeatingSystemChange("Heizkörper")}
                />
                <span>Heizkörper</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Fußbodenheizung und Heizkörper"
                  checked={propertyData.currentHeatingSystem === "Fußbodenheizung und Heizkörper"}
                  onChange={() => handleCurrentHeatingSystemChange("Fußbodenheizung und Heizkörper")}
                />
                <span>Fußbodenheizung und Heizkörper</span>
              </label>
            </div>
          </div>
        )}
        
        {/* Solarthermieanlage */}
        {progression >= 9 && (
          <div className="form-section">
            <label className="form-label required">Haben Sie eine Solarthermieanlage?</label>
            <div className="yes-no-buttons">
              <button 
                type="button"
                className={`yes-button ${propertyData.hasSolarThermal === true ? 'selected' : ''}`}
                onClick={() => handleSolarThermalChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path 
                      d="M4 12.6L9 17.6L20 6.6" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button 
                type="button"
                className={`yes-button ${propertyData.hasSolarThermal === false ? 'selected' : ''}`}
                onClick={() => handleSolarThermalChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path 
                      d="M5 5L19 19M5 19L19 5" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
          </div>
        )}

        {/* Solarthermie wird abgeklemmt oder abgebaut */}
        {progression >= 10 && propertyData.hasSolarThermal === true && (
          <div className="form-section">
            <label className="form-label required">Solarthermie wird abgeklemmt oder abgebaut?</label>
            <div className="yes-no-buttons">
              <button 
                type="button"
                className={`yes-button ${propertyData.solarThermalRemoved === true ? 'selected' : ''}`}
                onClick={() => handleSolarThermalRemovedChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path 
                      d="M4 12.6L9 17.6L20 6.6" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button 
                type="button"
                className={`no-button ${propertyData.solarThermalRemoved === false ? 'selected' : ''}`}
                onClick={() => handleSolarThermalRemovedChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path 
                      d="M5 5L19 19M5 19L19 5" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            
            {/* Qualifizierungsnachricht */}
            {propertyData.solarThermalRemoved === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}
        
        {/* Denkmalschutz */}
        {progression >= 11 && (
          <div className="form-section">
            <label className="form-label required">Denkmalschutz</label>
            <div className="yes-no-buttons">
              <button 
                type="button"
                className={`reverse-yes-button ${propertyData.hasMonumentProtection === true ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path 
                      d="M4 12.6L9 17.6L20 6.6" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button 
                type="button"
                className={`reverse-no-button ${propertyData.hasMonumentProtection === false ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path 
                      d="M5 5L19 19M5 19L19 5" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            
            {/* Qualifizierungsnachricht für Denkmalschutz */}
            {propertyData.hasMonumentProtection === true && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path 
                    d="M5 5L19 19M5 19L19 5" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Zeige abschließende Qualifizierungsmeldung wenn alle erforderlichen Felder ausgefüllt sind und Lead qualifiziert ist */}
        {progression >= 12 && !isLeadDisqualified() && isStep1Valid() && (
          <div className="qualification-message qualified">
            <svg viewBox="0 0 24 24" className="qualification-icon">
              <path 
                d="M4 12.6L9 17.6L20 6.6" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </svg>
            <span>Der Lead ist qualifiziert!</span>
          </div>
        )}
        
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={handleCancel}>
            Abbrechen
          </button>
          <button 
            type="button" 
            className="next-button"
            onClick={nextStep}
            disabled={!isStep1Valid() || isLeadDisqualified()}
          >
            Weiter
          </button>
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    return (
      <>
        <h3>Kundendaten</h3>
        
        <div className="form-row">
          <div className="form-field">
            <label>Anrede*</label>
            <select
              name="anrede"
              value={formData.anrede}
              onChange={handleChange}
              required
            >
              <option value="">Bitte wählen</option>
              <option value="herr">Herr</option>
              <option value="frau">Frau</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Vorname*</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-field">
            <label>Nachname*</label>
            <input
              type="text"
              name="nachname"
              value={formData.nachname}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>E-Mail</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="form-field">
            <label>Handy*</label>
            <input
              type="tel"
              name="handy"
              value={formData.handy}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-field">
            <label>Straße*</label>
            <input
              type="text"
              name="strasse"
              value={formData.strasse}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-field">
            <label>Hausnummer*</label>
            <input
              type="text"
              name="hausnummer"
              value={formData.hausnummer}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Postleitzahl*</label>
            <input
              type="text"
              name="postleitzahl"
              value={formData.postleitzahl}
              onChange={handleChange}
              pattern="[0-9]{5}"
              maxLength="5"
              required
            />
          </div>
          <div className="form-field">
            <label>Stadt*</label>
            <input
              type="text"
              name="stadt"
              value={formData.stadt}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="form-section consent-section">
          <div className="consent-text">
            <p>Bestätigung der Einwilligung zur telefonischen oder persönlichen Kontaktaufnahme durch {getConsentBranding(user).companyName} oder deren Vertragspartner zur Datenerhebung, -verarbeitung und Informationszusendung. Die Datenschutzinformationen der {getConsentBranding(user).companyName} sind hier einzusehen: <a href={getConsentBranding(user).datenschutzUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d72b8', wordBreak: 'break-all' }}>{getConsentBranding(user).datenschutzUrl}</a> <span style={{ color: 'red' }}>*</span></p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <OptInToggle
              mode={optInMode}
              onModeChange={handleOptInModeChange}
              signature={formData.signature}
              onSignatureChange={handleOptInSignatureChange}
              email={formData.email}
              required={true}
            />
          </div>
        </div>
        
        <div className="form-actions">
          <button type="button" onClick={prevStep}>
            Zurück
          </button>
          <button type="button" className="cancel-button" onClick={handleCancel}>
            Abbrechen
          </button>
          <button 
            type="submit" 
            disabled={loading || !formData.name || (optInMode === 'signature' && !formData.signature)}
          >
            {loading ? 'Wird gespeichert...' : 'Lead speichern'}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="lead-form">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSubmit}>
        {step === 1 ? renderStep1() : renderStep2()}
      </form>

      {/* Email-Warnung Modal */}
      {showEmailWarningModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '16px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: window.innerWidth <= 768 ? '20px 16px' : '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '16px', 
              color: '#d32f2f',
              fontSize: window.innerWidth <= 768 ? '18px' : '20px',
              lineHeight: '1.4'
            }}>
              ⚠️ E-Mail-Adresse ist leer
            </h3>
            <p style={{ 
              marginBottom: '20px', 
              color: '#666',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: '1.5'
            }}>
              Die E-Mail-Adresse ist leer. Möchten Sie diese automatisch mit einem Template füllen?
            </p>
            <p style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px', 
              fontFamily: 'monospace',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              wordBreak: 'break-all',
              overflowWrap: 'break-word'
            }}>
              {generateTemplateEmail(formData.name, formData.nachname)}
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowEmailWarningModal(false);
                  setPendingSubmit(null);
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Email mit Template füllen
                  const templateEmail = generateTemplateEmail(formData.name, formData.nachname);
                  // Email im State aktualisieren
                  setFormData(prev => ({ ...prev, email: templateEmail }));
                  setShowEmailWarningModal(false);
                  // Submit fortsetzen mit Template-Email
                  await submitLeadData(templateEmail);
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Ja, mit Template füllen
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowEmailWarningModal(false);
                  // Submit ohne Email fortsetzen
                  await submitLeadData();
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Nein, ohne E-Mail speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

export default WpForm;